import { useEffect, useLayoutEffect, useRef, useState, type FormEvent } from "react";
import {
  Volume2,
  Star,
  Flag,
  Bookmark,
  ArrowRight,
  RotateCcw,
  Check,
  SquarePen,
  Pause,
} from "lucide-react";
import {
  activeReviews,
  cloze,
  collocationExercise,
  dateLabel,
  dayKey,
  durationLabel,
  examplesFor,
  id,
  modeLabels,
  modes,
  normalizeAnswer,
  ratingLabels,
  target,
  lemmaKey,
  shiftDay,
  type Snapshot,
  type Mode,
  type StudySession,
  type ReviewEvent,
  type WordSense,
  type ExampleSentence,
} from "./domain";
import { db, recordReview, undoReview } from "./db";
import { buildQueue } from "./study";
import { schedule } from "./scheduler";
import { speak, speechAvailable } from "./platform";
import { Empty, IconButton, Panel, Stat } from "./components";

export default function Study({
  s,
  initialKind,
  autoStart = false,
  onEdit,
  notify,
  onLibrary,
}: {
  s: Snapshot;
  initialKind: "daily" | "due" | "difficult";
  autoStart?: boolean;
  onEdit: (w: WordSense) => void;
  notify: (m: string) => void;
  onLibrary: () => void;
}) {
  const [mode, setMode] = useState<Mode>(
    s.settings.mode === "collocation" && !s.settings.collocationTests
      ? "understand"
      : s.settings.mode,
  );
  const [kind, setKind] = useState(initialKind),
    [session, setSession] = useState<StudySession | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [held, setHeld] = useState(false),
    [resumeIndex, setResumeIndex] = useState(0),
    [lastId, setLastId] = useState<string | null>(null),
    [awaitContinue, setAwaitContinue] = useState<string | null>(null);
  const autoStarted = useRef(false);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(t);
  }, []);
  const reviews = activeReviews(s.events),
    sessionEvents = session ? reviews.filter((e) => e.sessionId === session.id) : [];
  const done = new Set(
      sessionEvents.filter((e) => e.id !== awaitContinue).map((e) => e.cardId),
    ),
    queue = session?.queue ?? [];
  const remaining = queue.filter((c) => !done.has(c)),
    cardId = remaining[0],
    card = s.cards.find((c) => c.id === cardId),
    word = s.words.find((w) => w.id === card?.wordId && !w.deleted);
  const wordExamples = word ? examplesFor(s, word.id) : [],
    example = wordExamples[sessionEvents.length % Math.max(1, wordExamples.length)];
  const queuePreview = buildQueue(s, mode, kind, now);
  const resumable = s.sessions
    .filter(
      (v) =>
        !v.endedAt &&
        v.id !== session?.id &&
        v.queue.some(
          (c) => !reviews.some((e) => e.sessionId === v.id && e.cardId === c),
        ),
    )
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0];
  async function start() {
    setBusy(true);
    setError("");
    try {
      if (mode === "listen" && !speechAvailable(s.settings))
        throw new Error(
          "Dinleme için cihazında yerel İngilizce ses gerekli. Ses ayarlarını kontrol et veya başka bir mod seç.",
        );
      const q = buildQueue(s, mode, kind, new Date());
      if (!q.length) return;
      const se: StudySession = {
        id: id(),
        startedAt: new Date().toISOString(),
        endedAt: null,
        mode,
        queue: q,
        newLimit: s.settings.dailyNew,
      };
      await db.sessions.add(se);
      setSession(se);
      setHeld(false);
      setLastId(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function stop() {
    if (session)
      await db.sessions.update(session.id, {
        endedAt: new Date().toISOString(),
      });
    setHeld(true);
  }
  async function undo() {
    if (!lastId) return;
    try {
      await undoReview(lastId);
      setResumeIndex((v) => v + 1);
      setHeld(false);
      setLastId(null);
      setAwaitContinue(null);
      notify("Son değerlendirme geri alındı.");
    } catch (e) {
      notify((e as Error).message);
    }
  }
  useEffect(() => {
    if (autoStart && !autoStarted.current) {
      autoStarted.current = true;
      void start();
    }
  }, [autoStart]);
  const completed = session && (!remaining.length || held);
  useLayoutEffect(() => {
    if (!session) return;
    const target = completed
      ? document.querySelector<HTMLElement>('[data-page-active="true"] h1')
      : document.querySelector<HTMLElement>(".flashcard");
    target?.focus({ preventScroll: true });
  }, [cardId, !!completed]);
  if (session && completed) {
    const firstByLemma = new Map<string, ReviewEvent>();
    for (const e of [...reviews].sort((x, y) => x.at.localeCompare(y.at))) {
      const w = s.words.find((w) => w.id === e.wordId);
      if (w && !firstByLemma.has(lemmaKey(w))) firstByLemma.set(lemmaKey(w), e);
    }
    const newWordIds = new Set(
        [...firstByLemma]
          .filter(([, e]) => e.sessionId === session.id)
          .map(([lemma]) => lemma),
      ),
      repeated = sessionEvents.filter((e) => e.before.memory.reps > 0).length,
      difficult = sessionEvents.filter((e) => e.effectiveRating <= 2).length,
      minutes = sessionEvents.reduce((n, e) => n + e.activeMs, 0) / 60000;
    const tomorrow = s.states
      .filter(
        (st) =>
          sessionEvents.some((e) => e.cardId === st.cardId) &&
          dayKey(st.memory.dueAt) === shiftDay(dayKey(new Date()), 1),
      )
      .map(
        (st) =>
          s.words.find((w) => w.id === s.cards.find((c) => c.id === st.cardId)?.wordId)
            ?.lemma,
      )
      .filter(Boolean);
    const learning = s.states.filter(
      (st) =>
        sessionEvents.some((e) => e.cardId === st.cardId) &&
        [1, 3].includes(st.memory.state),
    );
    return (
      <div className="stack">
        <div className="page-heading">
          <span className="eyebrow">Bugünkü emeğin</span>
          <h1 tabIndex={-1}>Oturum özeti</h1>
          <p>
            {held ? "Çalışmaya ara verdin." : "Bu oturumdaki kartları tamamladın."}{" "}
            Sonuçların cihazına kaydedildi.
          </p>
        </div>
        <div className="stats-grid">
          <Stat label="Cevaplanan kart" value={sessionEvents.length} />
          <Stat label="İlk kez çalışılan birim" value={newWordIds.size} />
          <Stat label="Tekrar" value={repeated} />
          <Stat label="Zorlanılan kart" value={difficult} />
          <Stat label="Aktif süre" value={durationLabel(minutes * 60000)} />
        </div>
        {learning.length > 0 && (
          <Panel title="Kısa tekrarlar devam ediyor">
            <p>
              Öğrenme aşamasındaki {learning.length} kartın yeniden zamanı gelecek. En
              yakın tekrar: {dateLabel(learning.map((st) => st.memory.dueAt).sort()[0])}
              .
            </p>
          </Panel>
        )}
        <Panel title="Yarın için planlananlar">
          <p>
            {tomorrow.length
              ? [...new Set(tomorrow)].join(", ")
              : "Bu oturumdan yarın için planlanmış kart yok."}
          </p>
          <small className="muted">
            Mevcut tekrar tarihlerine dayalı tahmin. Sonraki çalışmalarla değişebilir.
          </small>
        </Panel>
        <div className="toolbar">
          {lastId && (
            <button className="secondary" onClick={undo}>
              <RotateCcw size={17} />
              Son değerlendirmeyi geri al
            </button>
          )}
          <button
            className="primary"
            onClick={async () => {
              if (session)
                await db.sessions.update(session.id, {
                  endedAt: new Date().toISOString(),
                });
              setSession(null);
              setHeld(false);
            }}
          >
            Çalışma planına dön
          </button>
        </div>
      </div>
    );
  }
  if (session && word && example && card) {
    return (
      <div className="study-page stack">
        <div className="section-heading">
          <div>
            <span className="eyebrow">{modeLabels[session.mode]}</span>
            <h1 tabIndex={-1}>Kelime çalış</h1>
          </div>
          <button className="text-button" onClick={stop}>
            <Pause size={16} />
            Ara ver
          </button>
        </div>
        <div className="session-progress">
          <progress value={done.size} max={queue.length} />
          <span>
            {done.size} / {queue.length}
          </span>
        </div>
        <StudyCard
          key={cardId + ":" + resumeIndex}
          s={s}
          word={word}
          example={example}
          cardId={cardId}
          mode={session.mode}
          sessionId={session.id}
          onEdit={() => onEdit(word)}
          onSaved={(e) => {
            setLastId(e.id);
            if (!s.settings.autoNext) setAwaitContinue(e.id);
          }}
          notify={notify}
        />
        {awaitContinue && (
          <button className="primary" onClick={() => setAwaitContinue(null)}>
            Sonraki karta geç
            <ArrowRight size={18} />
          </button>
        )}
        {lastId && (
          <button className="text-button undo-study" onClick={undo}>
            <RotateCcw size={16} />
            Önceki değerlendirmeyi geri al
          </button>
        )}
      </div>
    );
  }
  if (session && !word) {
    return (
      <Empty
        title="Bu oturumun kartı artık kullanılamıyor"
        action={
          <button
            className="primary"
            onClick={async () => {
              await stop();
              setSession(null);
            }}
          >
            Çalışma planına dön
          </button>
        }
      >
        Kelime silinmiş veya cümlesi değiştirilmiş olabilir. Yeni bir oturum
        başlatabilirsin.
      </Empty>
    );
  }
  return (
    <div className="stack">
      <div className="page-heading">
        <h1 tabIndex={-1}>Kelime çalış</h1>
        <p>Modunu seç ve başla.</p>
      </div>
      <div className="segmented" aria-label="Çalışma kapsamı">
        {(
          [
            ["daily", "Günlük plan"],
            ["due", "Tekrarlar"],
            ["difficult", "Zor kelimeler"],
          ] as const
        ).map(([k, label]) => (
          <button key={k} aria-pressed={kind === k} onClick={() => setKind(k)}>
            {label}
          </button>
        ))}
      </div>
      <label className="mode-select">
        Çalışma modu
        <select value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
          {modes
            .filter((m) => m !== "collocation" || s.settings.collocationTests)
            .map((m) => (
              <option key={m} value={m}>
                {modeLabels[m]}
              </option>
            ))}
        </select>
      </label>
      {resumable && (
        <div className="notice">
          <p>Yarım kalan bir oturumun var: {modeLabels[resumable.mode]}.</p>
          <button
            className="secondary"
            onClick={() => {
              setMode(resumable.mode);
              setSession(resumable);
              setHeld(false);
            }}
          >
            Kaldığım yerden devam et
          </button>
        </div>
      )}
      <section className="study-ready">
        <div>
          <h2>
            {queuePreview.length
              ? `${queuePreview.length} kart hazır`
              : "Şu anda bekleyen kart yok"}
          </h2>
          <p>
            {kind === "due"
              ? "Yalnızca zamanı gelen tekrarlar."
              : kind === "difficult"
                ? "İşaretlediğin, sık unuttuğun veya sık ipucu kullandığın kelimeler."
                : "Önce zamanı gelen tekrarlar, ardından günlük hedefin kadar yeni kelime."}
          </p>
        </div>
        {queuePreview.length ? (
          <button className="primary" onClick={start} disabled={busy}>
            {busy ? "Hazırlanıyor…" : "Oturumu başlat"}
            <ArrowRight size={18} />
          </button>
        ) : (
          <button className="secondary" onClick={onLibrary}>
            Kelime ekle veya kütüphaneyi aç
          </button>
        )}
      </section>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <p className="footnote">
        Her yönün tekrar tarihi ayrıdır. Zor kelime oturumu plan tarihinden önce
        çalıştırabilir; yeni değerlendirme tekrar planını günceller.
      </p>
    </div>
  );
}

function StudyCard({
  s,
  word,
  example,
  cardId,
  mode,
  sessionId,
  onEdit,
  onSaved,
  notify,
}: {
  s: Snapshot;
  word: WordSense;
  example: ExampleSentence;
  cardId: string;
  mode: Mode;
  sessionId: string;
  onEdit: () => void;
  onSaved: (e: ReviewEvent) => void;
  notify: (m: string) => void;
}) {
  const state = s.states.find((v) => v.cardId === cardId)!;
  const originalState = useRef(state);
  const [prediction, setPrediction] = useState<ReviewEvent["prediction"] | null>(null),
    [meaning, setMeaning] = useState(false),
    [translation, setTranslation] = useState(
      s.settings.showTranslation && mode !== "produce",
    ),
    [translationUsed, setTranslationUsed] = useState(
      s.settings.showTranslation && mode !== "produce",
    ),
    [hint, setHint] = useState(s.settings.showTranslation && mode !== "produce"),
    [revealed, setRevealed] = useState(false),
    [answer, setAnswer] = useState(""),
    [correct, setCorrect] = useState<boolean | null>(null),
    [free, setFree] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [audio, setAudio] = useState(""),
    [heard, setHeard] = useState(false),
    [intervals, setIntervals] = useState<string[]>([]),
    [saved, setSaved] = useState(false);
  const active = useRef(0),
    answerTime = useRef(0),
    lastInteraction = useRef(performance.now()),
    lastTick = useRef(performance.now()),
    region = useRef<HTMLDivElement>(null),
    started = useRef(false);
  function captureTime() {
    const now = performance.now();
    if (
      document.visibilityState === "visible" &&
      !document.querySelector("dialog[open]") &&
      now - lastInteraction.current < 30000
    )
      active.current += Math.max(0, Math.min(now - lastTick.current, 1500));
    lastTick.current = now;
  }
  useEffect(() => {
    const interact = () => {
      captureTime();
      lastInteraction.current = performance.now();
    };
    const hidden = () => {
      captureTime();
      lastTick.current = performance.now();
      if (document.hidden && "speechSynthesis" in window) speechSynthesis.cancel();
    };
    window.addEventListener("pointerdown", interact);
    window.addEventListener("keydown", interact);
    document.addEventListener("visibilitychange", hidden);
    const t = setInterval(captureTime, 500);
    return () => {
      clearInterval(t);
      window.removeEventListener("pointerdown", interact);
      window.removeEventListener("keydown", interact);
      document.removeEventListener("visibilitychange", hidden);
      if ("speechSynthesis" in window) speechSynthesis.cancel();
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const now = new Date();
      const values = await Promise.all(
        [1, 2, 3, 4].map(async (g) => {
          const { after } = await schedule(state, g, now, s.settings.retention);
          const min = Math.max(
            1,
            Math.round((Date.parse(after.memory.dueAt) - +now) / 60000),
          );
          return min < 60
            ? min + " dk"
            : min < 1440
              ? Math.round(min / 60) + " sa"
              : (min / 1440 < 10
                  ? (min / 1440).toFixed(1).replace(".", ",")
                  : Math.round(min / 1440)) + " gün";
        }),
      );
      if (!cancelled) setIntervals(values);
    })().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [state.version, s.settings.retention]);
  const automatic =
    mode === "cloze" || mode === "collocation" || (mode === "produce" && !free);
  const chunk = word.collocations[0] ?? "",
    exercise = collocationExercise(word.lemma, chunk),
    clozeChunk = exercise.prompt;
  const expected =
    mode === "collocation"
      ? exercise.expected
      : mode === "produce"
        ? word.lemma
        : target(example);
  function reveal(e?: FormEvent) {
    e?.preventDefault();
    if (revealed) return;
    if (mode === "listen" && !heard) {
      setError(
        "Önce cümleyi dinle. Ses çalınamıyorsa çalışma planından başka bir mod seç.",
      );
      return;
    }
    if (automatic && !answer.trim()) {
      setError("Önce cevabını yaz veya “Hatırlayamadım” seçeneğini kullan.");
      return;
    }
    captureTime();
    answerTime.current = active.current;
    setCorrect(
      automatic ? normalizeAnswer(answer) === normalizeAnswer(expected) : null,
    );
    setRevealed(true);
    setMeaning(true);
    setPrediction(prediction ?? "unrecorded");
    setError("");
    requestAnimationFrame(() => region.current?.focus());
  }
  function forget() {
    setPrediction("forgot");
    setCorrect(automatic ? false : null);
    captureTime();
    answerTime.current = active.current;
    setRevealed(true);
    setMeaning(true);
    setError("");
    requestAnimationFrame(() => region.current?.focus());
  }
  function openWord() {
    if (!revealed) {
      setHint(true);
      if (!prediction) setPrediction("unrecorded");
    }
    setMeaning((v) => !v);
  }
  function openTranslation() {
    if (!translation) setTranslationUsed(true);
    if (!revealed && !translation) {
      setHint(true);
      if (!prediction) setPrediction("unrecorded");
    }
    setTranslation((v) => !v);
  }
  async function rate(rating: number) {
    if (started.current || saved) return;
    started.current = true;
    setBusy(true);
    setError("");
    try {
      captureTime();
      const e = await recordReview({
        cardId,
        exampleId: example.id,
        sessionId,
        mode,
        answer,
        prompt:
          mode === "collocation"
            ? exercise.prompt
            : mode === "produce"
              ? example.tr
              : mode === "cloze"
                ? cloze(example)
                : example.en,
        expected: automatic ? expected : undefined,
        correct,
        prediction: prediction ?? "unrecorded",
        rating,
        hint,
        translation: translationUsed,
        answerMs: Math.min(answerTime.current, 3600000),
        activeMs: Math.min(active.current, 3600000),
        expectedVersion: originalState.current.version,
      });
      setSaved(true);
      onSaved(e);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      started.current = false;
    }
  }
  function sentence() {
    let cursor = 0;
    const pieces: React.ReactNode[] = [];
    for (const [i, span] of example.spans.entries()) {
      pieces.push(example.en.slice(cursor, span.start));
      pieces.push(
        <button
          className="target-word"
          lang="en"
          aria-label={`${example.en.slice(span.start, span.end)} anlamını aç`}
          aria-expanded={meaning}
          key={i}
          onClick={openWord}
        >
          {example.en.slice(span.start, span.end)}
        </button>,
      );
      cursor = span.end;
    }
    pieces.push(example.en.slice(cursor));
    return pieces;
  }
  return (
    <article className="flashcard" tabIndex={-1} aria-label="Çalışma kartı">
      <header className="card-top">
        <span className="badge">
          {word.cefr} {word.kind === "phrase" ? "ifade" : word.pos}
        </span>
        <details className="card-tools">
          <summary aria-label="Kart seçenekleri">
            <span aria-hidden="true">•••</span>
          </summary>
          <div className="toolbar">
            <IconButton
              label={word.favorite ? "Favorilerden çıkar" : "Favorilere ekle"}
              pressed={word.favorite}
              onClick={() =>
                void db.words.update(word.id, { favorite: !word.favorite })
              }
            >
              <Star size={21} fill={word.favorite ? "currentColor" : "none"} />
            </IconButton>
            <IconButton
              label="Zor kelime olarak işaretle"
              pressed={word.difficult}
              onClick={() =>
                void db.words.update(word.id, { difficult: !word.difficult })
              }
            >
              <Flag size={21} fill={word.difficult ? "currentColor" : "none"} />
            </IconButton>
            <IconButton label="Kelimeyi düzenle veya yeni örnek ekle" onClick={onEdit}>
              <SquarePen size={21} />
            </IconButton>
          </div>
        </details>
      </header>
      <div className="card-prompt">
        {mode === "produce" ? (
          <>
            <p className="sentence translation-prompt" lang="tr">
              {example.tr}
            </p>
            <p className="prompt-help">
              {free
                ? "İngilizce bir cümle üret. Cevabı kendin değerlendireceksin."
                : `Hedef ${word.kind === "phrase" ? "ifadeyi" : "kelimeyi"} İngilizce yaz.`}
            </p>
          </>
        ) : mode === "collocation" ? (
          <>
            <p className="sentence" lang="en">
              {revealed ? chunk : clozeChunk}
            </p>
            <p className="prompt-help">
              Kayıtlı kalıbın eksik kelimesini tamamla. Hedef anlam: {word.meaning}
            </p>
          </>
        ) : mode === "listen" && !revealed ? (
          <div className="listening">
            <button
              className="listen-button"
              aria-label="Cümleyi dinle"
              onClick={() => {
                if (speak(example.en, s.settings, setAudio)) setHeard(true);
              }}
            >
              <Volume2 size={34} />
            </button>
            <p>Cümleyi dinle ve hedef kelimeyi tanımaya çalış.</p>
          </div>
        ) : (
          <p className="sentence" lang="en">
            {mode === "cloze" && !revealed ? cloze(example) : sentence()}
          </p>
        )}
        {mode !== "listen" && (
          <button
            className="text-button"
            onClick={() =>
              speak(
                revealed && mode === "collocation" ? chunk : example.en,
                s.settings,
                setAudio,
              )
            }
          >
            <Volume2 size={18} />
            Cümleyi dinle
          </button>
        )}
        <div role="status" className="audio-status">
          {audio}
        </div>
      </div>
      {(meaning || revealed) && (
        <div className="answer-panel" ref={region} tabIndex={-1}>
          <div className="section-heading">
            <div>
              <h2 lang="en">{word.lemma}</h2>
              <p>{word.meaning}</p>
            </div>
            <IconButton
              label="Kelimenin telaffuzunu dinle"
              onClick={() => speak(word.lemma, s.settings, setAudio)}
            >
              <Volume2 size={23} />
            </IconButton>
          </div>
          <p className="muted">
            <span lang="en">{word.ipa || "IPA eklenmemiş"}</span> · {word.pos}
          </p>
          {mode === "produce" && revealed && (
            <p lang="en" className="reference-sentence">
              Örnek: {example.en}
            </p>
          )}
          {s.settings.collocationExplanations && word.collocations.length > 0 && (
            <div className="collocation">
              <span>Yaygın kullanım</span>
              <strong lang="en">{word.collocations.join(" · ")}</strong>
              {word.commonError && <small>{word.commonError}</small>}
            </div>
          )}
          {word.notes && <p className="footnote">{word.notes}</p>}
          {revealed && (
            <p
              className={
                "feedback " +
                (correct === false
                  ? "incorrect"
                  : correct === true
                    ? "correct"
                    : "neutral")
              }
            >
              {correct === true ? (
                <>
                  <Check size={18} />
                  Hedef cevapla eşleşti.
                </>
              ) : correct === false ? (
                <>
                  Doğru hedef: <strong lang="en">{expected}</strong>. Yazım ve kelime
                  biçimini karşılaştır.
                </>
              ) : free ? (
                "Birden fazla doğru cümle olabilir. Bu örnek tek doğru cevap değildir; anlamını ve dilbilgisini kendin değerlendir."
              ) : (
                "Kendi yanıtını anlamla karşılaştır."
              )}
            </p>
          )}
        </div>
      )}
      {!revealed && (
        <div className="recall-area stack">
          <p className="prompt-help">Önce anlamını hatırla.</p>
          {mode === "produce" && (
            <label className="check-label">
              <input
                type="checkbox"
                checked={free}
                onChange={(e) => {
                  setFree(e.target.checked);
                  setAnswer("");
                }}
              />
              Serbest cümle üretimi (öz değerlendirme)
            </label>
          )}
          {(automatic || free) && (
            <form onSubmit={reveal} className="stack">
              <label>
                {free ? "İngilizce cümlen" : "İngilizce cevabın"}
                <input
                  lang="en"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoComplete="off"
                  placeholder={free ? "Kendi cümleni yaz" : "Hedef kelimeyi yaz"}
                />
              </label>
              <button className="primary" disabled={busy}>
                Cevabı kontrol et
              </button>
            </form>
          )}
          {!automatic && (
            <>
              <div className="prediction" aria-label="Cevap öncesi tahmin">
                {(
                  [
                    ["remembered", "Hatırladım"],
                    ["unsure", "Emin değilim"],
                    ["forgot", "Hatırlayamadım"],
                  ] as const
                ).map(([v, label]) => (
                  <button
                    className="secondary"
                    key={v}
                    aria-pressed={prediction === v}
                    disabled={hint}
                    onClick={() => setPrediction(v)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {!free && (
                <button className="primary full" onClick={() => reveal()}>
                  Cevabı göster
                </button>
              )}
            </>
          )}
          {(automatic || free) && (
            <button className="text-button" onClick={forget}>
              Hatırlayamadım, cevabı göster
            </button>
          )}
          {(hint || translation) && (
            <p className="hint-note">
              İpucu kullanıldı. Bu cevap ipucusuz hatırlama olarak sayılmayacak.
            </p>
          )}
        </div>
      )}
      {mode !== "produce" && (
        <button
          className="translation-toggle"
          aria-expanded={translation}
          onClick={openTranslation}
        >
          {translation ? "Türkçe çeviriyi kapat" : "Türkçe çeviriyi aç"}
        </button>
      )}
      {translation && mode !== "produce" && (
        <p className="translation-text">{example.tr}</p>
      )}
      {revealed && (
        <div className="rating-area">
          <p>İlk yanıtını nasıl değerlendiriyorsun?</p>
          {(hint || prediction === "forgot" || correct === false) && (
            <small className="muted">
              {correct === false || prediction === "forgot"
                ? "Tekrar planı “Tekrar” ile güncellenecek."
                : "İpucu kullanıldı. Plan: en fazla Zorlandım."}{" "}
            </small>
          )}
          <div className="ratings">
            {[1, 2, 3, 4].map((n) => (
              <button
                key={n}
                className={"rating rating-" + n}
                disabled={busy || saved}
                onClick={() => rate(n)}
              >
                <strong>{ratingLabels[n]}</strong>
                <small>
                  {intervals[
                    (correct === false || prediction === "forgot"
                      ? 1
                      : hint
                        ? Math.min(n, 2)
                        : n) - 1
                  ] ?? "…"}
                </small>
              </button>
            ))}
          </div>
          <details className="scheduling-help">
            <summary>Tekrar aralıkları hakkında</summary>
            <p>
              Aralıklar FSRS önizlemesidir; değerlendirme anında kesinleşir. Seçtiğin
              değerlendirme ayrıca saklanır.
            </p>
          </details>
        </div>
      )}
      <footer className="card-bottom">
        <button
          className="text-button"
          onClick={() =>
            notify(
              "Bu kelime zaten kütüphanende kayıtlı. Değerlendirmeler otomatik kaydedilir.",
            )
          }
        >
          <Bookmark size={17} />
          Kayıtlı
        </button>
        <span className="muted">
          {state.memory.reps
            ? "Sonraki tekrar: " + dateLabel(state.memory.dueAt)
            : "İlk çalışma"}
        </span>
      </footer>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </article>
  );
}
